import { useLoaderData } from '@remix-run/react';
import { memo, useState, type PropsWithChildren, type SVGProps, type FC, useEffect } from 'react';
import { Dialog, DialogContent, HiddenDialogTitle } from '~/components/ui/Dialog';
import { BoltToken } from '~/components/ui/icons/BoltToken';
import { clientFetch } from '~/lib/.client/fetch';
import { REFERRALS_FEATURE_ENABLED } from '~/lib/constants';
import { getReferrerId } from '~/lib/stores/location';
import type { loader } from '~/routes/_chat._index';
import type { ReferralInfo } from '~/routes/api.referrals.($id)';
import { LoginButton } from '~/routes/login';
import { formatTokens, formatTokensFullText } from '~/utils/format';

export const ReferredWelcomeDialog = memo(() => {
  const [referralData, setReferralData] = useState<ReferralInfo | undefined>();
  const { loggedIn } = useLoaderData<typeof loader>();
  const [open, setOpen] = useState(false);

  const username = referralData?.username ?? '<unknown>';
  const freeGrant = referralData?.freeGrant ?? 0;
  const subscriptionGrant = referralData?.subscriptionGrant ?? 0;

  useEffect(() => {
    if (loggedIn || !REFERRALS_FEATURE_ENABLED) {
      return;
    }

    const referredID = getReferrerId();

    if (!referredID) {
      return;
    }

    clientFetch(`/api/referrals/${referredID}`)
      .then((response) => response.json<ReferralInfo>())
      .then((data) => {
        setReferralData(data);
        setOpen(true);
      })
      .catch((error) => {
        // do not show a toast, this error should be silent
        console.error(error);
      });
  }, []);

  return (
    <Dialog
      open={open}
      maxWidth={676}
      onClose={() => setOpen(false)}
      showCloseButton
      closeOnBackdropClick
      backdropClassName="backdrop-blur"
    >
      <HiddenDialogTitle>Welcome to Bolt!</HiddenDialogTitle>
      <div className="h-40 group w-full relative bg-gradient-to-b from-[#002E4F00] to-accent-500">
        <div className="overflow-hidden size-full relative">
          <Card tokenCount={5_000_000} />
        </div>
        <Shape className="absolute bottom-0" fill="var(--bolt-elements-welcome-shape-fill)" />
        <Shape className="absolute -bottom-px" fill="var(--bolt-elements-bg-depth-2)" />
      </div>

      <DialogContent className="flex flex-col gap-3 items-center">
        <h2 className="font-600 text-lg">Welcome to bolt.new!</h2>

        <div>
          <Paragraph className="mb-4">
            You were referred by <b className="text-bolt-elements-textPrimary">{username}</b> 🎉
          </Paragraph>
          <Paragraph>
            You will receive <BoltToken className="inline-block" />{' '}
            <span className="text-bolt-elements-textPrimary">{formatTokens(freeGrant)} referral tokens</span> when you
            create your account
          </Paragraph>
          <Paragraph>
            plus an additional <BoltToken className="inline-block" />{' '}
            <span className="text-bolt-elements-textPrimary">{formatTokens(subscriptionGrant)} referral tokens</span> if
            you upgrade to a Pro plan within 30 days.
          </Paragraph>
          <Paragraph className="mt-4">Create your account now & enjoy your free bonus tokens instantly!</Paragraph>
        </div>

        <LoginButton samePageLogin variant="secondary" className="mt-2 py-2 mb-4">
          Create your account
        </LoginButton>
      </DialogContent>
    </Dialog>
  );
});

const Paragraph = ({ className = '', children }: PropsWithChildren<{ className?: string }>) => (
  <p className={`font-light text-center text-sm text-bolt-elements-textSecondary ${className}`}>{children}</p>
);

const Shape: FC<SVGProps<SVGPathElement>> = ({ className, ...props }) => (
  <svg className={`w-full h-auto ${className}`} viewBox="0 0 674 28" fill="none">
    <path
      d="M220.564 0.939941H-3V28.0601H333.749H674V0.939941H450.436C445.415 0.939941 440.46 2.08045 435.945 4.27537L415.573 14.178C411.058 16.3729 406.103 17.5134 401.082 17.5134H333.749H269.918C264.898 17.5134 259.942 16.3729 255.427 14.178L235.055 4.27537C230.54 2.08045 225.585 0.939941 220.564 0.939941Z"
      {...props}
    />
  </svg>
);

const OVERLAY_ID = 'paint0_radial_4739_1026';

const Overlay: FC<SVGProps<SVGPathElement>> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 454 215" fill="none" className={className}>
    <path opacity="0.2" d="m400,0l-54,215l-400,0l0,-215l454,0z" fill={`url(#${OVERLAY_ID})`} />
    <defs>
      <radialGradient
        id={OVERLAY_ID}
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(128.76 197.92) rotate(-122.683) scale(257.195 329.986)"
      >
        <stop stopColor="#D9D9D9" />
        <stop offset="1" stopColor="#D9D9D9" stopOpacity="0" />
      </radialGradient>
    </defs>
  </svg>
);

const Card: FC<{ tokenCount: number }> = ({ tokenCount }) => (
  <div
    className="absolute transition-all group-hover:top-[20px] left-1/2 -translate-x-1/2 top-[40px] w-77 h-48 rounded-xl border-px border-bolt-elements-borderColor border-solid bg-bolt-elements-background-depth-2 overflow-hidden"
    style={{
      boxShadow:
        '0px 4px 4px 0px var(--bolt-elements-welcome-shadow-color-1) inset, 1px 4px 5.6px 0px var(--bolt-elements-welcome-shadow-color-2)',
    }}
  >
    <h2 className="font-700 text-lg p-3">
      <BoltToken className="inline-block" />{' '}
      <span className="bg-clip-text text-transparent bg-gradient-linear bg-gradient-[90deg,var(--un-gradient-stops)] from-bolt-elements-textPrimary to-[#999] text-base">
        {formatTokensFullText(tokenCount)} Tokens
      </span>
    </h2>
    <div className="absolute inset-0 flex justify-center top-6">
      <img src="/static/bolt-card-bg.svg" className="h-fit w-40" width="163" height="205" />
    </div>
    <img src="/static/bolt-card-logo.svg" className="absolute top-4 right-3" />
    <Overlay className="absolute top-0 transition-transform -translate-x-2/5 group-hover:translate-x-0 h-full" />
  </div>
);
